export interface PriceBreakdown {
  base: { label: string; pricePerDay: number; days: number; amount: number };
  pickup_location: { name: string; surcharge: number };
  return_location: { name: string; surcharge: number };
  extras: Array<{ name: string; price: number }>;
  deposit: number;
  total: number;
}

export interface BuildBreakdownInput {
  pricePerDay: number;
  days: number;
  pickup_location: { name: string; surcharge: number };
  return_location: { name: string; surcharge: number };
  extras: Array<{ name: string; price: number }>;
  deposit?: number | null;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Build a price breakdown. Pure — takes only primitives. `total` is the full
 * amount the customer pays up front (base + surcharges + extras + deposit). The
 * refundable `deposit` IS part of `total`: it is charged together with the
 * rental (online via Stripe, or with the on-site/transfer payment) and later
 * returned by the lessor via bank transfer once the rental ends — see the
 * Stripe charge in stripe.ts. It is still exposed as its own field so receipts
 * can show it as a distinct, refundable line item. All monetary values rounded
 * to 2 decimals to avoid IEEE-754 display bleed. Pickup and return surcharges
 * are charged independently, even if the same location is chosen for both.
 */
export function buildBreakdown(input: BuildBreakdownInput): PriceBreakdown {
  const base = round2(input.pricePerDay * input.days);
  const pickupSurcharge = round2(input.pickup_location.surcharge);
  const returnSurcharge = round2(input.return_location.surcharge);
  const extras = input.extras.map((e) => ({ name: e.name, price: round2(e.price) }));
  const extrasTotal = extras.reduce((sum, e) => sum + e.price, 0);
  const deposit = round2(input.deposit ?? 0);
  // Deposit included — charged up front with the rental, refunded by bank
  // transfer at the end. Kept as its own field for the refundable line item.
  const total = round2(base + pickupSurcharge + returnSurcharge + extrasTotal + deposit);

  return {
    base: {
      label: `${input.days} d. × ${input.pricePerDay} €`,
      pricePerDay: input.pricePerDay,
      days: input.days,
      amount: base,
    },
    pickup_location: { name: input.pickup_location.name, surcharge: pickupSurcharge },
    return_location: { name: input.return_location.name, surcharge: returnSurcharge },
    extras,
    deposit,
    total,
  };
}
